/**
 * Where this area can be, and which of those places each person is offered.
 *
 * The rule is the desktop app's, and it holds here too: **the rail lists
 * places, and tabs are views of one thing.** A section that contains other
 * places opens underneath itself rather than pushing a second rail onto the
 * page, and only one is open at a time. The rail never disappears.
 *
 * There are four shells, not one. Which is on screen is a question of *where
 * you are*, never of who you are:
 *
 * - `personal` — an account holding no seat anywhere. It has no cloud: nothing
 *   is published, so there is no workspace to list, no setting to distribute
 *   and no integration to connect on somebody else's behalf. Three rows, and
 *   it is the only place `Licence` and `Invoices` exist.
 * - `org` — an organization, with the rows that organization owns.
 * - `workspace` — entering one *replaces* the rail, the way opening a
 *   repository on GitHub replaces its owner's. You land on Projects: a
 *   workspace has no overview of its own, its rows *are* what it contains.
 * - `system` — the deployment, for an address in `AIRLOCK_ADMIN_EMAILS`. A
 *   shell of its own rather than a fourth group, so that no rail has to spend
 *   the same drawing on two rows: there are thirteen glyphs and thirteen is
 *   what the navigation needs.
 *
 * `system` is also the one shell that *replaces* another: an address that
 * administers the deployment has no `personal` shell behind it. That account
 * buys nothing — no licence, no invoice, no machine of its own — so the place
 * under its own name is the deployment, and it is where it lands.
 *
 * What a role does *not* have is absent, never greyed. The Worker answers a
 * non-member **404 and not 403** — which of the two you are is itself
 * information about who is in the organization — and a menu of refusals would
 * give that away in the one place the API refuses to.
 *
 * ## Every rail sorts by the question its groups answer
 *
 * A rail used to be a flat run of rows that changed subject and changed back.
 * Now all three of them are grouped in the same order, with the same headings,
 * so moving between an organization and one of its workspaces costs nothing:
 *
 * ```
 *                     where the work is, and who does it   (the org's name, or nothing)
 * CONFIGURATION       how it is set up
 * ACCOUNT · JOURNAL   what you consult rather than change
 * ```
 *
 * What was actually wrong: `Audit` and `Billing` sat between the people and the
 * settings, though nobody comes to either of them to work; and a workspace kept
 * `Integrations` and `Secrets` out among its places — they are configuration —
 * with `History` cutting that configuration in half.
 */

export type PageId =
  /* the identity menu, top right — what is mine is under my own name, which is
     why none of these three is a rail row anywhere */
  | 'machines'
  | 'downloads'
  | 'profile'
  /** The affiliate programme: this account's link, what it earned and its payouts. */
  | 'affiliate'
  /* a personal account */
  | 'overview'
  | 'licence'
  | 'invoices'
  /* the organization */
  | 'workspaces'
  | 'teams'
  | 'team'
  | 'members'
  /** Every member's linked machines, and cutting one off — an owner's or an administrator's. */
  | 'orgMachines'
  | 'audit'
  /** What the members' agents spent and what came of it — every member's own, an administrator's for all. */
  | 'usage'
  /** Every run of the members' agents in the organization's workspaces — a member's own, an administrator's for all. */
  | 'runs'
  /** What the agents cost, by member, team, workspace, agent or model, and the monthly budgets — an administrator's. */
  | 'spend'
  /** Where the audit and the runs are streamed to — an administrator's. */
  | 'streams'
  | 'billing'
  | 'orgSettings'
  | 'orgWorkflows'
  /** How the Rules audits of the organization's workspaces went — the rules themselves are each workspace's. */
  | 'orgRules'
  | 'orgAgents'
  | 'orgIntegrations'
  /** The organization's own secrets, and the machines waiting for its key — names and numbers, never a value. */
  | 'orgSecrets'
  /** Who an Enterprise organization's people are: domains, single sign-on, provisioning, factors — an owner's or an administrator's. */
  | 'orgSecurity'
  /* inside one workspace */
  | 'wsProjects'
  | 'wsAccess'
  | 'wsIntegrations'
  | 'wsSecrets'
  | 'wsHistory'
  | 'wsSettings'
  | 'wsAgents'
  /** The workspace's own rules repository, the same way. */
  | 'wsRules'
  /* the deployment */
  | 'adminOverview'
  | 'adminAccounts'
  | 'adminAccountDetail'
  | 'adminOrgs'
  | 'adminOrgDetail'
  | 'adminAffiliates'
  | 'adminRequests';

export type ShellKind = 'personal' | 'org' | 'workspace' | 'system';

/**
 * Which pages each shell holds.
 *
 * Which shell is open is a fact about *state* — whether a workspace has been
 * entered, whether this account holds a seat — and not about the page, which
 * is why this is a membership test rather than a `Record<PageId, ShellKind>`.
 * `overview` is deliberately in two of them: a personal account's overview and
 * an organization's are the same place in the navigation and two different
 * answers, and giving them two ids would put two rows in one rail.
 */
const ANYWHERE: readonly PageId[] = ['machines', 'downloads', 'profile', 'affiliate'];

/**
 * What the identity menu holds, per shell.
 *
 * The same three everywhere but the deployment. `Machines` is the list of
 * installs *a licence* has, and a system administrator holds none — offering
 * it would open a page whose own sentence ("the machines this licence is
 * installed on") names something this account cannot have.
 */
const MENU_OF: Record<ShellKind, readonly PageId[]> = {
  personal: ANYWHERE,
  org: ANYWHERE,
  workspace: ANYWHERE,
  system: ['downloads', 'profile']
};

const PAGES_OF: Record<ShellKind, readonly PageId[]> = {
  // `usage` has no row on a personal rail: its address explains where the statistics are instead.
  personal: ['overview', 'licence', 'invoices', 'usage'],
  org: ['overview', 'workspaces', 'teams', 'team', 'members', 'orgMachines', 'audit', 'usage', 'runs', 'spend', 'streams', 'billing', 'orgSettings', 'orgWorkflows', 'orgRules', 'orgAgents', 'orgIntegrations', 'orgSecrets', 'orgSecurity'],
  workspace: ['wsProjects', 'wsAccess', 'wsIntegrations', 'wsSecrets', 'wsHistory', 'wsSettings', 'wsAgents', 'wsRules'],
  system: ['adminOverview', 'adminAccounts', 'adminAccountDetail', 'adminOrgs', 'adminOrgDetail', 'adminAffiliates', 'adminRequests']
};

/**
 * Whether a page can be drawn in the shell that is open.
 *
 * The pages the identity menu holds belong to no shell: they are reached from
 * a menu that is on every header, and sending somebody back to their
 * organization's rail to read their own profile would be a shell change for
 * something that is not a change of place.
 */
export function allowedIn(shell: ShellKind, page: PageId): boolean {
  return MENU_OF[shell].includes(page) || PAGES_OF[shell].includes(page);
}

/** Where a shell opens when nothing else named a page. */
export function landingOf(shell: ShellKind): PageId {
  // A workspace has no overview of its own: its rows *are* what it contains,
  // so entering one lands on Projects, as opening a repository lands on code.
  return shell === 'workspace' ? 'wsProjects' : shell === 'system' ? 'adminOverview' : 'overview';
}

/**
 * The page's own name, and the sentence under it where it needs one.
 *
 * The header names the **page** and not what the breadcrumb names already:
 * "Acme / airlock-desktop" is in the rail, so the header says "Workflows".
 * Three of these are titled by whichever section is open instead — see
 * `titleOf`.
 */
export const PAGES: Record<PageId, { title: string; lede?: string }> = {
  machines: { title: 'Machines', lede: 'The machines this licence is installed on. Removing one frees its slot immediately.' },
  downloads: { title: 'Downloads', lede: 'The macOS build for Apple silicon — the trial as well as the one your key updates from.' },
  profile: { title: 'Profile', lede: 'Your name, email, sign-in methods and password.' },
  affiliate: { title: 'Affiliate', lede: 'Share your link and earn half of what every customer you bring pays, for as long as they pay.' },

  overview: { title: 'Overview' },
  licence: { title: 'Licence' },
  invoices: { title: 'Invoices', lede: 'Every invoice Stripe has issued on this account.' },

  workspaces: { title: 'Workspaces' },
  teams: { title: 'Teams' },
  team: { title: 'Team' },
  members: { title: 'Members' },
  orgMachines: { title: 'Machines', lede: 'Every machine your members have linked, and the ones this organization has cut off.' },
  audit: { title: 'Audit', lede: 'Who did what to this organization’s workspaces, settings, secrets and machines.' },
  usage: { title: 'Statistics', lede: 'What the agents spent, what came of it, and what that says.' },
  streams: { title: 'Streaming', lede: 'Endpoints this organization’s audit and runs are sent to, signed — a SIEM, a log pipeline, a Slack channel.' },
  spend: { title: 'Spend', lede: 'What the agents cost at list prices, where it went, and the monthly budgets set against it.' },
  runs: { title: 'Runs', lede: 'Every agent run in this organization’s workspaces: who, which model, what came of it, what it cost — never what was asked or written.' },
  billing: { title: 'Billing' },
  orgSettings: { title: 'Settings' },
  orgWorkflows: { title: 'Workflows' },
  orgRules: { title: 'Rules', lede: 'How the Rules audits of this organization’s workspaces went. The rules themselves are each workspace’s.' },
  orgAgents: { title: 'Agents' },
  orgIntegrations: { title: 'Integrations' },
  orgSecrets: { title: 'Secrets' },
  orgSecurity: { title: 'Security' },

  wsProjects: { title: 'Projects' },
  wsAccess: { title: 'Access' },
  wsIntegrations: { title: 'Integrations' },
  wsSecrets: { title: 'Secrets' },
  wsHistory: { title: 'History' },
  wsSettings: { title: 'Settings' },
  wsAgents: { title: 'Agents' },
  wsRules: { title: 'Rules', lede: 'The rules this workspace is held to, as its rules repository holds them.' },

  adminOverview: { title: 'System overview', lede: 'Every tenant in the deployment, at a glance.' },
  adminAccounts: { title: 'Accounts', lede: 'Who administers this deployment, and everybody it serves.' },
  adminAccountDetail: { title: 'Account', lede: 'One account: its licence, its invoices, its organizations and its trail.' },
  adminOrgs: { title: 'Organizations', lede: 'Seats, utilization and revenue per organization.' },
  adminOrgDetail: { title: 'Organization', lede: 'Seats, activity and the actions support can take.' },
  adminAffiliates: { title: 'Affiliates', lede: 'Who earns what, the signs of abuse, and which payouts go out.' },
  adminRequests: { title: 'Requests', lede: 'What people sent from the app. Nothing goes on to a workflow until it is passed on here.' }
};

/* ── what a section holds ──────────────────────────────────────────────────
 *
 * Three entries per category and six links at most. A single catch-all
 * "Organization settings" made a wall of twenty rows broken by four
 * subheadings; these are the desktop app's own families, in its own order, so
 * that somebody who has already configured their app finds the same names in
 * the same place.
 *
 * `Logs` sits among the children of Settings rather than beside them: the
 * desktop app files it under Housekeeping, and one page does not need to be a
 * place of its own.
 *
 * Three of the desktop's pages are deliberately missing — `General`,
 * `License` and `About`. `appearance`, `confirmDestroy` and `preventSleep`
 * cannot be overridden: they are one person's preferences on one machine, and
 * showing them greyed would be writing "here is something that is not here".
 */
export interface SettingsSection {
  key: string;
  label: string;
  /** The sentence the page opens with, from the desktop panel of the same name. */
  lede: string;
}

export const SETTINGS_SECTIONS: SettingsSection[] = [
  { key: 'sessions', label: 'Sessions', lede: 'How a session starts, how long it waits for you, and what it does when it is done.' },
  { key: 'sandbox', label: 'Sandbox', lede: 'Where a session’s copy of the workspace runs.' },
  { key: 'network', label: 'Network', lede: 'What a session may reach, and what may reach it.' },
  { key: 'git', label: 'Git & merge', lede: 'How work leaves a session and lands on a branch.' },
  { key: 'audit', label: 'Audit', lede: 'What a change is checked against while it is written, and what draws its map.' },
  { key: 'notifications', label: 'Notifications', lede: 'When Airlock interrupts, and when it waits to be asked.' },
  { key: 'logs', label: 'Logs', lede: 'What is kept on disk, and for how long.' }
];

/** The agents, in the order the desktop app's own group has them. */
export const AGENT_SECTIONS: SettingsSection[] = [
  { key: 'all', label: 'All agents', lede: 'Which agents a member may launch, and what each one is handed.' },
  { key: 'claude-code', label: 'Claude Code', lede: 'The whole of Claude Code’s configuration, as the app accepts it.' },
  { key: 'codex', label: 'Codex', lede: 'The whole of Codex’s configuration, as the app accepts it.' }
];

/** The integrations an organization connects once, for every workspace to use. */
export const INTEGRATION_SECTIONS: SettingsSection[] = [
  { key: 'jira', label: 'Jira', lede: 'The Jira site this organization reads tickets from.' },
  { key: 'trello', label: 'Trello', lede: 'Trello boards, read the same way Jira’s are.' },
  { key: 'github', label: 'GitHub', lede: 'The account Airlock clones, pushes and opens pull requests as.' },
  { key: 'ngrok', label: 'ngrok', lede: 'The account a session’s tunnel runs under — a tunnel runs as an account, and accounts are integrations.' },
  { key: 'bookmarklet', label: 'Bookmarklet', lede: 'A page open in a browser becomes a task in one click. There is nothing to connect — only whether it is offered.' },
  { key: 'add', label: 'Add an integration', lede: 'What else this organization can connect.' }
];

/**
 * The one agents page a *workspace* has.
 *
 * An organization's `Agents` row opens three sections, and two of them are an
 * agent's own configuration document — `agentSettings`, which travels in the
 * six an organization hands a new install and which no workspace overrides. A
 * workspace has the other page: seven of the overridables describe
 * how an agent runs (autonomously or asking, from the host config or from
 * nothing, pinned or updating, Preview and its probes), and until this row
 * existed they were reachable here only by typing JSON.
 */
export const WORKSPACE_AGENT_SECTIONS: SettingsSection[] = [
  { key: 'all', label: 'All agents', lede: 'How an agent runs in this workspace’s sessions, whichever one a member launches.' }
];

/**
 * What an Enterprise organization says about who its people are. Each is an
 * Enterprise feature (`EnterpriseOnly` draws the notice in its place on Team),
 * and the whole page is an owner's or an administrator's: the Worker answers
 * 404 to a member on every route behind it.
 */
export const SECURITY_SECTIONS: SettingsSection[] = [
  { key: 'domains', label: 'Domains', lede: 'The domains this organization has proved, and who an address at one lets in.' },
  { key: 'sso', label: 'Single sign-on', lede: 'Your people sign in through your identity provider, with OIDC or SAML.' },
  { key: 'scim', label: 'Provisioning', lede: 'Your identity provider adds, removes and groups your people here, with SCIM.' },
  { key: 'twofactor', label: 'Two-factor', lede: 'Whether every member needs a code from their phone to sign in.' }
];

/** Every section a page can be opened on, by page. */
export const SECTIONS_OF: Partial<Record<PageId, SettingsSection[]>> = {
  orgSettings: SETTINGS_SECTIONS,
  orgAgents: AGENT_SECTIONS,
  orgIntegrations: INTEGRATION_SECTIONS,
  orgSecurity: SECURITY_SECTIONS,
  wsSettings: SETTINGS_SECTIONS,
  wsAgents: WORKSPACE_AGENT_SECTIONS
};

/** What the header says: the open section where there is one, else the page. */
export function titleOf(page: PageId, section: string | null): string {
  const found = SECTIONS_OF[page]?.find((entry) => entry.key === section);
  return found ? found.label : PAGES[page].title;
}

export function ledeOf(page: PageId, section: string | null): string | undefined {
  const found = SECTIONS_OF[page]?.find((entry) => entry.key === section);
  return found ? found.lede : PAGES[page].lede;
}

/** The section a page opens on when none was named. */
export function firstSection(page: PageId): string | null {
  return SECTIONS_OF[page]?.[0]?.key ?? null;
}

/* ── the rails ─────────────────────────────────────────────────────────── */

export interface RailChild {
  key: string;
  label: string;
  /** A connection's state, where the child is one: teal for on, grey for off. */
  dot?: 'on' | 'off';
}

export interface RailRow {
  page: PageId;
  label: string;
  icon: string;
  children?: RailChild[];
}

export interface RailGroup {
  /** Uppercase, mono, over the rows it owns. The organization's group is
   *  labelled with the organization's own name — the rail lists what belongs
   *  to it, and saying so is what makes the identity menu's absence read. */
  label?: string;
  rows: RailRow[];
}

/** What each of the rows with children is offered, given what is connected. */
export interface RailState {
  /** Which agents this organization has configured. */
  agents?: Record<string, boolean>;
  /** Which integrations it has connected. */
  integrations?: Record<string, boolean>;
}

const dotted = (sections: SettingsSection[], on: Record<string, boolean> | undefined, dotless: string[] = []): RailChild[] =>
  sections.map((section) => ({
    key: section.key,
    label: section.label,
    dot: dotless.includes(section.key) ? undefined : on?.[section.key] ? 'on' : 'off'
  }));

/**
 * The same, minus what is off — for integrations, and for agents.
 *
 * An integration an organization has not turned on is not a place it has: the
 * page behind the row is one switch and a sentence, and listing five of those
 * under Integrations made a rail of things this organization does *not* do.
 * What a role does not have is absent rather than greyed, and this is the same
 * rule applied to a capability — turning one on is what puts it on the rail,
 * and *Add an integration* is where that is done.
 *
 * The dot stays for the ones that are on, because "on" and "finished setting
 * up" are two states and the difference is worth a colour: an organization can
 * switch Jira on and not have named the site yet.
 *
 * An agent reads the same way. Its page is the whole of its configuration and
 * nothing else — the switch is on *All agents* — so an agent that is not
 * offered is a page of settings for something no member can launch.
 */
const connected = (sections: SettingsSection[], on: Record<string, boolean> | undefined, always: string[]): RailChild[] =>
  dotted(sections, on, always).filter((child) => always.includes(child.key) || child.dot === 'on');

const plain = (sections: SettingsSection[]): RailChild[] =>
  sections.map((section) => ({ key: section.key, label: section.label }));

/** A group with no rows is not a heading over nothing: it is dropped. */
const filled = (groups: RailGroup[]): RailGroup[] => groups.filter((group) => group.rows.length > 0);

/**
 * The organization's rail, in three groups.
 *
 * `Account` is last because nobody comes to Billing or Audit to work: they are
 * what you consult afterwards. Between the two groups, `Configuration` holds
 * every row that decides how the work is set up — which is where Workflows
 * belongs too, being one list of instructions the whole organization shares.
 *
 * ## What a member has, and why it is most of it
 *
 * `Configuration` used to be an administrator's group, and a member's rail was
 * two rows: Overview and Workspaces. The argument was that a menu of things you
 * cannot change is a menu of refusals. It was the wrong reading of what these
 * pages *are*. An organization's settings are not its private business — they
 * are **what every member's own machine is handed**: how long a session waits,
 * how much memory its sandbox gets, which agents may be launched, which of them
 * is frozen so that nobody can change it locally. A member who wonders why
 * their sandbox is 8 GB, or why a setting in their app will not take, had
 * nowhere to look and one thing to do about it: ask somebody. The Worker has
 * always answered `GET /settings` and `GET /workflows` to every seat, for
 * exactly that reason — the install reads them on every check. The rail was the
 * only thing hiding them.
 *
 * So a member gets the whole of `Configuration`, to **read**: the rows, the
 * values, and the padlock on the ones locked to them. Publishing stays an
 * owner's and an administrator's — `putOrgSettings` and `putOrgWorkflows` answer
 * 404 to anybody else — and the pages simply hold no verb for a reader, the way
 * a workspace with a read grant already does.
 *
 * Two groups stay an administrator's, and they are the two that are about
 * *other people* rather than about this member's machine:
 *
 * - **Billing and Audit.** What the organization pays and who did what. Neither
 *   describes the reader's own install, and the trail names every other member
 *   by email.
 * - **Teams, Members and Machines.** Who is in what is information about the organization,
 *   not configuration of it — and the Worker answers 404 and not 403 there on
 *   purpose (`requireOrgAdminRole`). A row leading to that 404 would give away
 *   the one thing the refusal is written to withhold.
 *
 * A member's *Integrations* is the connected ones and nothing else: no *Add an
 * integration*, since adding one is a publish they cannot make, and a row whose
 * only child is a door they may not open is worse than no row.
 */
export function orgRail(orgName: string, admin: boolean, state: RailState = {}): RailGroup[] {
  return filled([
    { rows: [{ page: 'overview', label: 'Overview', icon: 'dashboard' }] },
    {
      label: orgName,
      rows: [
        { page: 'workspaces', label: 'Workspaces', icon: 'projects' },
        // Every member's: a member reads their own days, an administrator
        // everybody's — the Worker scopes it, so the row is the same for both.
        { page: 'usage', label: 'Statistics', icon: 'adminOverview' },
        // The same split as Statistics: a member reads their own runs, an
        // administrator everybody's, and the Worker decides which.
        { page: 'runs', label: 'Runs', icon: 'runs' },
        ...(admin
          ? ([
              { page: 'teams', label: 'Teams', icon: 'org' },
              { page: 'members', label: 'Members', icon: 'seats' },
              // Beside the people whose machines they are, and like them an
              // administrator's: the Worker answers 404 to a member here too.
              { page: 'orgMachines', label: 'Machines', icon: 'devices' }
            ] as RailRow[])
          : [])
      ]
    },
    {
      label: 'Configuration',
      rows: [
        { page: 'orgSettings', label: 'Settings', icon: 'settings', children: plain(SETTINGS_SECTIONS) },
        // An agent this organization does not offer is not a page it has: the
        // rule integrations follow below, applied to the other capability on
        // this rail. `All agents` is what is always there, because that is
        // where an agent is offered or withdrawn.
        { page: 'orgAgents', label: 'Agents', icon: 'adminOverview', children: connected(AGENT_SECTIONS, state.agents, ['all']) },
        { page: 'orgWorkflows', label: 'Workflows', icon: 'org' },
        // Every member's: the rules their machine is held to. Its glyph is the
        // audit's — a rule is what an audit reads a change against.
        { page: 'orgRules', label: 'Rules', icon: 'audit' },
        // Every member's, like the rest of the group: a member reads the
        // organization secrets that reach them and their own machines waiting
        // for the key. The glyph is a workspace's Secrets row's.
        { page: 'orgSecrets', label: 'Secrets', icon: 'plan' },
        // A row with no children at all is dropped rather than drawn onto a
        // page of switches this reader cannot flip: an organization that has
        // connected nothing has no integrations for a member to consult.
        ...(admin || connected(INTEGRATION_SECTIONS, state.integrations, []).length > 0
          ? ([
              {
                page: 'orgIntegrations',
                label: 'Integrations',
                icon: 'adminOrgs',
                children: connected(INTEGRATION_SECTIONS, state.integrations, admin ? ['add'] : [])
              }
            ] as RailRow[])
          : []),
        // Who may sign in, and how — about other people, so an administrator's
        // like Members, and the Worker answers a member 404 behind it.
        ...(admin ? ([{ page: 'orgSecurity', label: 'Security', icon: 'plan', children: plain(SECURITY_SECTIONS) }] as RailRow[]) : [])
      ]
    },
    {
      label: 'Account',
      rows: admin
        ? ([
            { page: 'billing', label: 'Billing', icon: 'billing' },
            // Money too, and about other people: what each member's agents cost.
            { page: 'spend', label: 'Spend', icon: 'spend' },
            { page: 'audit', label: 'Audit', icon: 'audit' },
            // The audit and the runs, copied out to the organization's own systems.
            { page: 'streams', label: 'Streaming', icon: 'streams' }
          ] as RailRow[])
        : []
    }
  ]);
}

/** The identity menu's pages, which belong to no rail. */
export function menuPagesOf(shell: ShellKind): readonly PageId[] {
  return MENU_OF[shell];
}

/**
 * A personal account's rail: three rows, and no cloud.
 *
 * `Licence` and `Invoices` live here and nowhere else. An organization's owner
 * is not offered a personal licence — their team is what is billed — and
 * somebody holding a seat is not offered one either, because the seat *is*
 * the licence. There is no door from here to an organization: the kind is
 * chosen at sign-up, and the rail does not offer to change it.
 */
export function personalRail(): RailGroup[] {
  return [
    {
      rows: [
        { page: 'overview', label: 'Overview', icon: 'dashboard' },
        { page: 'licence', label: 'Licence', icon: 'plan' },
        { page: 'invoices', label: 'Invoices', icon: 'billing' }
      ]
    }
  ];
}

/**
 * A workspace's rail — the same three groups, in the same order.
 *
 * No overview: entering one lands on Projects, and a row summarising the others
 * would answer nothing they do not already say. No integrations *group* either:
 * the organization connects an account once and each workspace turns it on, so
 * what is here is a list of what this workspace uses, not of accounts.
 *
 * There is deliberately **no Workflows row**. A workflow belongs to no
 * workspace: the organization shares one list with every member, drawn on the
 * rail above this one. A row here would ask which workspace a chain called
 * *Feature* is for, and the answer is all of them.
 *
 * `Agents` is here and is not the organization's Agents: an agent's own
 * configuration document is handed to an install once and is never a
 * workspace's, so what this row opens is the seven overridables that say how an
 * agent *runs* — see `WORKSPACE_AGENT_SECTIONS`.
 */
export function workspaceRail(): RailGroup[] {
  return [
    {
      rows: [
        { page: 'wsProjects', label: 'Projects', icon: 'projects' },
        { page: 'wsAccess', label: 'Access', icon: 'seats' }
      ]
    },
    {
      label: 'Configuration',
      rows: [
        { page: 'wsSettings', label: 'Settings', icon: 'settings', children: plain(SETTINGS_SECTIONS) },
        { page: 'wsAgents', label: 'Agents', icon: 'adminOverview', children: plain(WORKSPACE_AGENT_SECTIONS) },
        { page: 'wsIntegrations', label: 'Integrations', icon: 'adminOrgs' },
        { page: 'wsSecrets', label: 'Secrets', icon: 'plan' },
        { page: 'wsRules', label: 'Rules', icon: 'audit' }
      ]
    },
    { label: 'Journal', rows: [{ page: 'wsHistory', label: 'History', icon: 'audit' }] }
  ];
}

/** The deployment's rail, for a system administrator. */
export function systemRail(): RailGroup[] {
  return [
    {
      rows: [
        { page: 'adminOverview', label: 'Overview', icon: 'adminOverview' },
        { page: 'adminAccounts', label: 'Accounts', icon: 'seats' },
        { page: 'adminOrgs', label: 'Organizations', icon: 'adminOrgs' },
        { page: 'adminAffiliates', label: 'Affiliates', icon: 'spend' },
        { page: 'adminRequests', label: 'Requests', icon: 'adminOverview' }
      ]
    }
  ];
}

/** Which row a page lights up, for the pages that have no row of their own. */
export const ROW_OF: Partial<Record<PageId, PageId>> = {
  team: 'teams',
  adminAccountDetail: 'adminAccounts',
  adminOrgDetail: 'adminOrgs'
};
